import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Check, 
  Crown, 
  Calendar, 
  Clock,
  Shield,
  Zap,
  Star,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Abbonamento() {
  const { t } = useTranslation();
  const { user, token, refreshUser } = useAuth();
  const [plans, setPlans] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [processing, setProcessing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadData();
    // Check if returning from Stripe checkout
    checkReturnFromStripe();
  }, [token]);

  const checkReturnFromStripe = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const status = urlParams.get('status');
    
    if (sessionId && status === 'success') {
      setCheckingPayment(true);
      // Poll payment status
      await pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === 'cancelled') {
      toast.info('Pagamento annullato');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Timeout verifica pagamento. Controlla la tua email.');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Errore verifica pagamento');
      
      const data = await res.json();
      
      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Pagamento completato! Abbonamento attivato.');
        loadData();
        refreshUser();
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Sessione di pagamento scaduta. Riprova.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const loadData = async () => {
    try {
      // Load plans
      const plansRes = await fetch(`${BACKEND_URL}/api/subscription/plans`);
      const plansData = await plansRes.json();
      setPlans(plansData.plans || {});

      // Load subscription status
      const subRes = await fetch(`${BACKEND_URL}/api/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const subData = await subRes.json();
      setSubscription(subData.subscription);

      // Load payment history
      const payRes = await fetch(`${BACKEND_URL}/api/payments/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const payData = await payRes.json();
      setPayments(payData.payments || []);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setProcessing(true);
    try {
      // Create Stripe checkout session
      const res = await fetch(`${BACKEND_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_type: selectedPlan,
          origin_url: window.location.origin
        })
      });

      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore durante la creazione del pagamento');
        setProcessing(false);
      }
    } catch (error) {
      toast.error('Errore di connessione');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Abbonamento disdetto con successo');
        setCancelDialogOpen(false);
        loadData();
        refreshUser();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore durante la disdetta');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading || checkingPayment) {
    return (
      <Layout>
        <div className="page-container flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5F1F] mb-4" />
          <p className="text-white font-semibold">
            {checkingPayment ? 'Verifica pagamento in corso...' : 'Caricamento...'}
          </p>
          {checkingPayment && (
            <p className="text-[#666] text-sm mt-2">Non chiudere questa pagina</p>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="subscription-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Metodi di Pagamento</h1>
            <p className="page-subtitle">Gestisci il tuo abbonamento Vault Ride</p>
          </div>
        </div>

        {/* Current Subscription Status */}
        {subscription ? (
          <Card className="vault-card mb-8 border-[#FF5F1F]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="w-6 h-6 text-[#FF5F1F]" />
                  <CardTitle className="text-white font-['Teko'] text-2xl uppercase">
                    Abbonamento Attivo
                  </CardTitle>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setCancelDialogOpen(true)}
                  className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                >
                  Disdici Abbonamento
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-[#666] text-sm">Piano</p>
                  <p className="text-white font-semibold">
                    {subscription.plan_type === 'monthly' ? 'Mensile' : 'Annuale'}
                  </p>
                </div>
                <div>
                  <p className="text-[#666] text-sm">Stato</p>
                  <p className="text-green-500 font-semibold flex items-center gap-1">
                    <Check className="w-4 h-4" /> Attivo
                  </p>
                </div>
                <div>
                  <p className="text-[#666] text-sm">Data Inizio</p>
                  <p className="text-white font-mono">{formatDate(subscription.start_date)}</p>
                </div>
                <div>
                  <p className="text-[#666] text-sm">Scadenza</p>
                  <p className="text-white font-mono">{formatDate(subscription.end_date)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="vault-card mb-8 border-[#262626]">
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-[#666]" />
                <div>
                  <p className="text-white font-semibold">Nessun abbonamento attivo</p>
                  <p className="text-[#666] text-sm">Scegli un piano per sbloccare tutte le funzionalità</p>
                </div>
              </div>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
              >
                Abbonati ora
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Subscription Plans */}
        <h2 className="text-xl font-['Teko'] text-white uppercase mb-4">Piani Disponibili</h2>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Monthly Plan */}
          <Card className={`vault-card cursor-pointer transition-all ${
            selectedPlan === 'monthly' ? 'border-[#FF5F1F]' : 'border-[#262626]'
          }`} onClick={() => setSelectedPlan('monthly')}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white font-['Teko'] text-xl uppercase">
                  Piano Mensile
                </CardTitle>
                <Calendar className="w-5 h-5 text-[#FF5F1F]" />
              </div>
              <CardDescription className="text-[#666]">
                Flessibilità massima
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 mb-4">
                <span className="text-4xl font-bold text-white font-mono">{plans.monthly?.price?.toFixed(2) || '2.00'}</span>
                <span className="text-[#666] mb-1">CHF/mese</span>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-[#9CA3AF]">
                  <Check className="w-4 h-4 text-green-500" />
                  Tutte le funzionalità
                </li>
                <li className="flex items-center gap-2 text-[#9CA3AF]">
                  <Check className="w-4 h-4 text-green-500" />
                  Backup su Google Drive
                </li>
                <li className="flex items-center gap-2 text-[#9CA3AF]">
                  <Check className="w-4 h-4 text-green-500" />
                  Cancella quando vuoi
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Annual Plan */}
          <Card className={`vault-card cursor-pointer transition-all relative ${
            selectedPlan === 'annual' ? 'border-[#FF5F1F]' : 'border-[#262626]'
          }`} onClick={() => setSelectedPlan('annual')}>
            <div className="absolute -top-3 right-4 bg-[#FF5F1F] text-white text-xs font-bold px-3 py-1 rounded-full">
              RISPARMIA 33%
            </div>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white font-['Teko'] text-xl uppercase">
                  Piano Annuale
                </CardTitle>
                <Star className="w-5 h-5 text-[#FF5F1F]" />
              </div>
              <CardDescription className="text-[#666]">
                Miglior rapporto qualità/prezzo
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 mb-4">
                <span className="text-4xl font-bold text-white font-mono">{plans.annual?.price?.toFixed(2) || '18.00'}</span>
                <span className="text-[#666] mb-1">CHF/anno</span>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-[#9CA3AF]">
                  <Check className="w-4 h-4 text-green-500" />
                  Tutte le funzionalità
                </li>
                <li className="flex items-center gap-2 text-[#9CA3AF]">
                  <Check className="w-4 h-4 text-green-500" />
                  Backup su Google Drive
                </li>
                <li className="flex items-center gap-2 text-[#9CA3AF]">
                  <Check className="w-4 h-4 text-green-500" />
                  Equivalente a {((plans.annual?.price || 18) / 12).toFixed(2)} CHF/mese
                </li>
                <li className="flex items-center gap-2 text-[#9CA3AF]">
                  <Check className="w-4 h-4 text-green-500" />
                  Supporto prioritario
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {!subscription && (
          <div className="text-center">
            <Button 
              onClick={() => setDialogOpen(true)}
              size="lg"
              className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-lg px-8"
            >
              <Crown className="w-5 h-5 mr-2" />
              Abbonati al Piano {selectedPlan === 'monthly' ? 'Mensile' : 'Annuale'}
            </Button>
          </div>
        )}

        {/* Payment History */}
        {payments.length > 0 && (
          <>
            <h2 className="text-xl font-['Teko'] text-white uppercase mt-12 mb-4">Storico Pagamenti</h2>
            <div className="space-y-2">
              {payments.map((payment) => (
                <div 
                  key={payment.id}
                  className="flex items-center justify-between p-4 bg-[#121212] border border-[#262626]"
                >
                  <div className="flex items-center gap-4">
                    <CreditCard className="w-5 h-5 text-[#666]" />
                    <div>
                      <p className="text-white">
                        Piano {payment.plan_type === 'monthly' ? 'Mensile' : 'Annuale'}
                      </p>
                      <p className="text-[#666] text-sm">{formatDate(payment.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-mono">{payment.amount} {payment.currency}</p>
                    <p className={`text-sm ${payment.status === 'completed' ? 'text-green-500' : 'text-yellow-500'}`}>
                      {payment.status === 'completed' ? 'Completato' : 'In attesa'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Payment Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                Conferma Pagamento
              </DialogTitle>
              <DialogDescription className="text-[#9CA3AF]">
                Stai per attivare il piano {selectedPlan === 'monthly' ? 'Mensile' : 'Annuale'}
              </DialogDescription>
            </DialogHeader>

            <div className="bg-[#121212] p-4 rounded border border-[#262626] my-4">
              <div className="flex justify-between items-center">
                <span className="text-[#9CA3AF]">Piano</span>
                <span className="text-white font-semibold">
                  {selectedPlan === 'monthly' ? 'Mensile' : 'Annuale'}
                </span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-[#9CA3AF]">Totale</span>
                <span className="text-[#FF5F1F] font-bold text-xl font-mono">
                  {selectedPlan === 'monthly' ? (plans.monthly?.price?.toFixed(2) || '2.00') : (plans.annual?.price?.toFixed(2) || '18.00')} CHF
                </span>
              </div>
            </div>

            <div className="bg-[#121212]/50 p-4 rounded border border-[#262626] mb-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-[#FF5F1F]" />
                <div>
                  <p className="text-white font-semibold">Pagamento Sicuro con Stripe</p>
                  <p className="text-[#666] text-sm">Carte di credito/debito, Apple Pay, Google Pay</p>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-[#262626] text-[#9CA3AF]"
              >
                Annulla
              </Button>
              <Button 
                onClick={handleSubscribe}
                disabled={processing}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reindirizzamento...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Procedi al Pagamento
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                Conferma Disdetta
              </DialogTitle>
              <DialogDescription className="text-[#9CA3AF]">
                Sei sicuro di voler disdire il tuo abbonamento?
              </DialogDescription>
            </DialogHeader>

            <div className="bg-[#121212] p-4 rounded border border-[#262626] my-4">
              <p className="text-[#9CA3AF] text-sm">
                Il tuo accesso premium rimarrà attivo fino alla data di scadenza: 
                <strong className="text-white ml-1">
                  {subscription ? formatDate(subscription.end_date) : '-'}
                </strong>
              </p>
              <p className="text-[#666] text-sm mt-2">
                Dopo questa data, perderai l'accesso alle funzionalità premium.
              </p>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setCancelDialogOpen(false)}
                className="border-[#262626] text-[#9CA3AF]"
              >
                Mantieni Abbonamento
              </Button>
              <Button 
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="bg-red-600 hover:bg-red-700"
              >
                {cancelling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Elaborazione...
                  </>
                ) : (
                  'Conferma Disdetta'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
